// Хуки данных. Экраны используют только их — кэш, повторы и инвалидация живут здесь.
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  ENTITIES, companyRef, contactName, contactRef, dealRef, isVesselContact, vesselName, vesselRef,
} from '../lib/entities'
import type { EntityKind, EntityRef } from '../lib/entities'
import { PAY, PAY_LIST_SELECT, PAY_TYPE_ID } from '../lib/payments'
import { makeRefs } from '../lib/refs'
import type { RefsApi } from '../lib/refs'
import * as api from './client'
import { CoreUnsupported, Unauthorized } from './client'
import { списокЗаписей } from './smartOwn'
import type { CrmItem, Deal, FieldMeta, ListParams, ListResult, Me, Payment } from './types'

/**
 * Не мучить сервер повторами, если сессия кончилась — пусть экран редиректит.
 * «Ядро не умеет» тоже не повторяем: ответ детерминированный, три попытки его не изменят.
 */
const retry = (count: number, err: unknown) =>
  !(err instanceof Unauthorized) && !(err instanceof CoreUnsupported) && count < 2

/** Ответ /api/me целиком (пользователь + права). Один запрос на всё приложение. */
const meQuery = { queryKey: ['me'], queryFn: api.fetchMe, staleTime: 5 * 60_000 } as const

/**
 * Кто вошёл. `null` — не вошли, на этом держатся RequireAuth и Login.
 *
 * `select` берёт из ответа только пользователя, поэтому у этого хука смысл ровно тот же,
 * что был до появления прав, а запрос — тот же самый (ключ кэша один, второй раз в сеть
 * не уходит).
 */
export const useMe = () =>
  useQuery({ ...meQuery, select: (d: Me | null) => d?.user ?? null })

/**
 * Права: зона, предупреждения, закрытые разделы, право записи (`rights` в /api/me).
 *
 * `undefined`/`null` — прав в ответе нет (старый BFF или RIGHTS_ENFORCE=0 на заглушке).
 * Тогда интерфейс НИЧЕГО не прячет: подсказки нет, а ограничение всё равно на сервере.
 * Прятать разделы «на всякий случай» нельзя — это выглядело бы как «раздела не существует».
 */
export const useRights = () =>
  useQuery({ ...meQuery, select: (d: Me | null) => d?.rights ?? null })

/** Готовые ответы на вопросы экранов о правах. Всё, что не знаем, трактуем как «можно». */
export interface RightsApi {
  /** Права пришли. false — ничего не прячем и ничего не обещаем. */
  loaded: boolean
  role: string
  roleTitle: string
  roleNote: string
  /** Выборка сужена зоной хоть где-то (`scope.kind !== 'all'`). */
  limited: boolean
  /** Зона пуста: человек вошёл, но записей ему не видно. Это НЕ «данных нет». */
  empty: boolean
  /** Почему зона именно такая — словами BFF. */
  basis: string
  /** Предупреждения BFF, готовые к показу как есть. */
  warnings: string[]
  /** Писать в CRM можно. Без прав — true: кнопку прячет сервер отказом, а не мы догадкой. */
  canWrite: boolean
  /** Раздел закрыт роли (код из rights.РАЗДЕЛЫ). Без прав всегда false. */
  denied: (code?: string) => boolean
  /** Выборка в разделе сужена по зоне. */
  scoped: (code?: string) => boolean
  /** «Раздел показывает заведомо неполную выборку» — текст BFF или пустая строка. */
  partial: (code?: string) => string
}

export const useRightsApi = (): RightsApi => {
  const { data } = useRights()
  return useMemo(() => {
    const r = data ?? null
    const byCode = new Map((r?.sections ?? []).map((s) => [s.code, s]))
    const denied = new Set(r?.denied_sections ?? [])
    return {
      loaded: !!r,
      role: r?.role ?? '',
      roleTitle: r?.role_title ?? '',
      roleNote: r?.role_note ?? '',
      limited: !!r && r.scope?.kind !== 'all',
      empty: !!r?.scope?.empty,
      basis: r?.scope?.basis ?? '',
      warnings: r?.warnings ?? [],
      canWrite: r ? r.can_write : true,
      denied: (code) => (!!r && !!code && denied.has(code)),
      scoped: (code) => (!!code && !!byCode.get(code)?.scoped),
      partial: (code) => (code ? byCode.get(code)?.partial || '' : ''),
    }
  }, [data])
}

export const useDeals = (params: ListParams) =>
  useQuery({
    queryKey: ['deals', params],
    queryFn: () => api.listDeals(params),
    retry,
    // список сделок меняется часто — держим свежесть, но без агрессивного поллинга
    staleTime: 30_000,
    placeholderData: (prev) => prev,   // не мигать пустотой при смене страницы/фильтра
  })

export const useDeal = (id: string | undefined) =>
  useQuery({
    queryKey: ['deal', id],
    queryFn: () => api.getDeal(id!),
    enabled: !!id,
    retry,
  })

export const useCompanies = (params: ListParams) =>
  useQuery({
    queryKey: ['companies', params],
    queryFn: () => api.listCompanies(params),
    retry,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })

export const useCompany = (id: string | undefined) =>
  useQuery({
    queryKey: ['company', id],
    queryFn: () => api.getCompany(id!),
    enabled: !!id,
    retry,
  })

export const useContacts = (params: ListParams) =>
  useQuery({
    queryKey: ['contacts', params],
    queryFn: () => api.listContacts(params),
    retry,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })

export const useContact = (id: string | undefined) =>
  useQuery({
    queryKey: ['contact', id],
    queryFn: () => api.getContact(id!),
    enabled: !!id,
    retry,
  })

/**
 * Записи смарт-процесса любого типа. Отдельный хук, а не useDeals с другим методом:
 * у смарт-процессов другой формат ответа (`result.items`) и обязательный entityTypeId,
 * это приведено в client.listItems.
 *
 * Тип — параметр, потому что читаются все типы одинаково: различается только состав полей,
 * а он приходит справочником `/fields`, а не зашит в код (см. lib/smart.ts).
 *
 * `enabled` нужен вкладкам в карточках: пока id компании не пришёл, запрос не имеет смысла.
 *
 * Источник — не только зеркало: у перенесённых типов (31, 1160, 153, 170) запрос может
 * уйти в свою схему. Решает api/smartOwn.ts тем же переключателем, что у сделок
 * (api/istochnik.ts); непереводимый запрос и неперенесённый тип честно читают зеркало.
 * Дебиторки (134) это не касается: она перенесена платежами, а не документами.
 */
export const useItems = <T = CrmItem>(entityTypeId: number, params: ListParams, enabled = true) =>
  useQuery({
    queryKey: ['items', entityTypeId, params],
    queryFn: () => списокЗаписей<T>(entityTypeId, params),
    enabled,
    retry,
    staleTime: 30_000,
    placeholderData: (prev: ListResult<T> | undefined) => prev,
  })

/**
 * Платежи (смарт-процесс 134) — дебиторка. Обёртка над useItems: ключ кэша и параметры
 * те же, что были, поэтому дебиторка ничего не заметила.
 */
export const usePayments = (params: ListParams, enabled = true) =>
  useItems<Payment>(PAY_TYPE_ID, params, enabled)

/**
 * Платежи ОДНОЙ СДЕЛКИ — для вкладки дебиторки в карточке сделки.
 *
 * Отбор по `parentId2` (системный родитель смарт-процесса), а не по полю-привязке
 * `ufCrm22Deal`. Причина в данных, а не в удобстве. У смарт-процесса 134 родитель
 * заполнен у 16 252 живых записей из 16 253, а поле-привязка — у 16 144 (28.07.2026).
 * Запись, где есть ТОЛЬКО поле-привязка, ровно одна. Ядро умеет в фильтре лишь «И», то
 * есть «одно ИЛИ другое» одним запросом не выразить; вторым запросом ради одной записи
 * из 16 тысяч платить двумя обращениями к ядру на каждое открытие карточки не стоит.
 * Экран пишет, что такая запись существует и здесь не покажется.
 *
 * Порядок — по «Примерной дате оплаты» (тот же, что в карточке компании и в разделе
 * «Дебиторка»): ближайший срок первым, потому что вкладку открывают ради «что горит».
 *
 * Оплаченные НЕ отфильтрованы намеренно, в отличие от карточки компании. У компании
 * платежей сотни, и нужен долг; у сделки их единицы (максимум на живых данных — 15),
 * и там нужна история расчётов: «за что уже заплатили, за что нет». Долг при этом
 * считает `summarize` из lib/payments.ts — он оплаченные в сумму не берёт.
 */
export const useDealPayments = (dealId: string | undefined) =>
  usePayments({
    filter: { parentId2: dealId },
    order: { [PAY.dueDate]: 'ASC' },
    select: PAY_LIST_SELECT,
  }, !!dealId)

/** Одна запись смарт-процесса целиком — со всеми UF-полями (в списке их не просят). */
export const useItem = (entityTypeId: number, id: string | undefined) =>
  useQuery({
    queryKey: ['item', entityTypeId, id],
    queryFn: () => api.getItem(entityTypeId, id!),
    enabled: !!id,
    staleTime: 60_000,
    retry,
  })

/**
 * Названия компаний пачкой: id → название.
 *
 * Зачем. В платеже есть только companyId, а в списке 50 строк. EntityLink название не грузит
 * намеренно (это были бы 50 запросов), поэтому берём их ОДНИМ запросом с фильтром-списком —
 * ядро понимает `{ID: [...]}` как IN. Больше 50 id за раз не спрашиваем: страница ядра — 50,
 * остальное молча не пришло бы.
 */
export const useCompanyTitles = (ids: Array<string | number | null | undefined>) => {
  const key = useMemo(() => {
    const uniq = new Set<string>()
    for (const v of ids) {
      const s = v === null || v === undefined ? '' : String(v)
      if (s && s !== '0') uniq.add(s)
    }
    return [...uniq].sort().slice(0, 50)
  }, [ids])

  return useQuery({
    queryKey: ['company-titles', key],
    enabled: key.length > 0,
    staleTime: 10 * 60_000,
    retry,
    queryFn: async (): Promise<Record<string, string>> => {
      const r = await api.listCompanies({ filter: { ID: key }, select: ['ID', 'TITLE'] })
      const out: Record<string, string> = {}
      for (const c of r.items) if (c.TITLE) out[String(c.ID)] = c.TITLE
      return out
    },
  })
}

/**
 * Поиск сущности для выпадающего выбора (EntityPicker) и для общего поиска в шапке.
 * Приводит разные форматы ядра к одному виду EntityRef, чтобы вызывающий не знал,
 * что сделки приходят сырым Bitrix, а суда — витриной ядра.
 */
export const useEntitySearch = (kind: EntityKind, q: string) => {
  const s = q.trim()
  const enabled = s.length >= ENTITIES[kind].minQuery
  // Подсказка у сделки — стадия. Показывать её кодом («C4:PREPAYMENT_INVOICE») бессмысленно:
  // справочник у нас есть, подставляем название. Без справочника останется код — это честно.
  const refs = useRefs()
  return useQuery({
    queryKey: ['entity-search', kind, s],
    enabled,
    staleTime: 60_000,
    retry,
    queryFn: async (): Promise<EntityRef[]> => {
      const order = { DATE_MODIFY: 'DESC' } as const
      if (kind === 'deal') {
        const r = await api.listDeals({ filter: { '%TITLE': s }, order, select: ['ID', 'TITLE', 'STAGE_ID'] })
        return r.items.map((d) => ({ ...dealRef(d), hint: refs.stageName(d.STAGE_ID) }))
      }
      if (kind === 'company') {
        const r = await api.listCompanies({ filter: { '%TITLE': s }, order, select: ['ID', 'TITLE'] })
        return r.items.map(companyRef)
      }
      if (kind === 'contact') {
        // %FULL_NAME — колонка ядра «Имя Отчество Фамилия», её нет в самом Bitrix.
        // Раньше искали по %LAST_NAME, и «Иван Петров» не находился вообще.
        const r = await api.listContacts({
          filter: { '%FULL_NAME': s }, order,
          select: ['ID', 'NAME', 'LAST_NAME', 'SECOND_NAME', 'COMPANY_ID'],
        })
        // Суда — тоже контакты, но у них своя сущность, своя витрина и своя карточка.
        // Не убрать их здесь — и одно судно приедет двумя строками: в «Контакты» и в «Суда».
        return r.items.filter((c) => !isVesselContact(c)).map(contactRef)
      }
      const rows = await api.searchVessels(s)
      return rows.map(vesselRef)
    },
  })
}

/**
 * Название сущности по её ID — для одиночных мест: карточка показывает, к какой компании
 * относится сделка. НЕ использовать в списках: это отдельный запрос на каждый вызов,
 * пятьдесят строк дадут пятьдесят запросов. В списках название приходит вместе с данными.
 */
export const useEntityTitle = (kind: EntityKind, id: string | undefined) =>
  useQuery({
    queryKey: ['entity-title', kind, id],
    enabled: !!id,
    staleTime: 10 * 60_000,   // названия меняются редко
    retry,
    queryFn: async (): Promise<string | null> => {
      if (kind === 'company') return (await api.getCompany(id!)).TITLE || null
      if (kind === 'deal') return (await api.getDeal(id!)).TITLE || null
      if (kind === 'contact') return contactName(await api.getContact(id!))
      // Судно — это контакт с фамилией «Судно», и его id совпадает с id контакта.
      // Через /vessels искать нельзя: та витрина ищет по названию и IMO, а не по id.
      return vesselName(await api.getContact(id!))
    },
  })

export const useVesselSearch = (q: string) =>
  useQuery({
    queryKey: ['vessels', q],
    queryFn: () => api.searchVessels(q),
    enabled: q.trim().length >= 2,   // ядро всё равно вернёт пусто на 1 символ
    staleTime: 5 * 60_000,
  })

/**
 * Справочники (воронки, стадии, сотрудники). Тянем один раз на сессию: ядро само держит
 * их в памяти часами, а список сотрудников обновляет синк. Ошибку НЕ поднимаем в экраны —
 * refs.loaded=false, и всё показывается сырыми ID (см. lib/refs.ts).
 */
export const useRefsQuery = () =>
  useQuery({ queryKey: ['refs'], queryFn: api.fetchRefs, staleTime: 30 * 60_000, retry })

export const useRefs = (): RefsApi => {
  const { data } = useRefsQuery()
  return useMemo(() => makeRefs(data), [data])
}

/**
 * Справочник полей по области (`company`, `deal`, `item:134`…).
 *
 * Зачем. В Bitrix 3712 полей с машинными кодами: без справочника карточка показала бы
 * `ufCrm22_1713163359` вместо «Номер счёта». Названия меняются раз в месяцы, поэтому
 * держим их долго и ошибку наружу не поднимаем: нет справочника — покажем код,
 * это честнее, чем пустое место.
 */
export const useFieldsQuery = (entity: string) =>
  useQuery({
    queryKey: ['fields', entity],
    queryFn: () => api.fetchFields(entity),
    enabled: !!entity,
    staleTime: 60 * 60_000,
    retry,
  })

export interface FieldsApi {
  loaded: boolean
  /** Справочник давно не обновлялся — названия могут отставать от Bitrix. */
  stale: boolean
  /** Человеческое название поля. Нет в справочнике — вернём сам код. */
  title: (code: string) => string
  /**
   * Название из справочника, а если его там нет — своё запасное.
   * Нужно для полей, которые в Bitrix никто не назвал: справочник честно отдаёт код,
   * и на экране получалось бы «UF_CRM_1508500822: 9123456» вместо «IMO».
   */
  titleOr: (code: string, fallback: string) => string
  meta: (code: string) => FieldMeta | undefined
  /**
   * Все поля области в порядке Bitrix (`sort`, потом код).
   *
   * Нужно разделам, которые НЕ знают состав полей заранее: список смарт-процесса строит
   * по нему колонки и фильтры. Дебиторка этим не пользуется — там коды выяснены и зашиты.
   */
  list: Array<{ code: string; meta: FieldMeta }>
  /** Значение перечисления текстом. Нет карты — вернём как есть. */
  enumText: (code: string, value: unknown) => string
  /** Писать нельзя: системное поле либо значение считает бизнес-процесс. */
  readonly: (code: string) => boolean
  /**
   * ПОЧЕМУ писать нельзя — словами ядра («запись в это поле запускает бизнес-процесс
   * Bitrix», «значение считает бизнес-процесс Bitrix», «системное поле Bitrix»).
   *
   * Нужен формам записи. `readonly` отвечает только «да/нет», а человеку у выключенного
   * поля надо объяснить причину: три причины ядра различаются по цене ошибки — «число
   * перезапишут через секунду» и «раздадут задания живым людям» — не одно и то же
   * (`BLOCK_REASON` в core_api.py). Пустая строка — поле писать можно.
   */
  readonlyReason: (code: string) => string
}

export const useFields = (entity: string): FieldsApi => {
  const { data } = useFieldsQuery(entity)
  return useMemo(() => {
    const map = data?.entities?.[entity] ?? {}
    const meta = (code: string) => map[code]
    // Порядок — как в Bitrix: сначала `sort`, при равном — по коду, чтобы список полей
    // не прыгал между отрисовками (у UF-полей sort часто одинаковый).
    const list = Object.entries(map)
      .map(([code, m]) => ({ code, meta: m }))
      .sort((a, b) => (a.meta.sort ?? 0) - (b.meta.sort ?? 0) || a.code.localeCompare(b.code))
    return {
      loaded: Object.keys(map).length > 0,
      stale: !!data?.fields_stale,
      meta,
      list,
      title: (code) => meta(code)?.title || code,
      titleOr: (code, fallback) => {
        const t = meta(code)?.title
        // ядро подставляет код вместо пустого названия — это и значит «не назвали»
        return t && t !== code ? t : fallback
      },
      readonly: (code) => !!meta(code)?.readonly,
      readonlyReason: (code) => {
        const m = meta(code)
        if (!m?.readonly) return ''
        // Причина есть только у полей, закрытых блок-листом сделок. У прочих readonly
        // приходит из самого Bitrix, и своей формулировки у ядра для них нет.
        return m.readonly_reason || 'поле только для чтения по справочнику Bitrix'
      },
      enumText: (code, value) => {
        if (value === null || value === undefined || value === '') return ''
        const m = meta(code)?.enum_map
        const key = String(value)
        return (m && m[key]) || key
      },
    }
  }, [data, entity])
}

export const useHealth = () =>
  useQuery({ queryKey: ['health'], queryFn: api.fetchHealth, refetchInterval: 60_000, retry })

/** Правка сделки: после успеха освежаем карточку и списки. */
export const useUpdateDeal = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fields: Record<string, unknown>) => api.updateDeal(id, fields),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['deal', id] })
      void qc.invalidateQueries({ queryKey: ['deals'] })
    },
  })
}

// ─────────────────── создание сделки: проверка, запись, перечитка ───────────────────
//
// Это ЕДИНСТВЕННАЯ запись, которую CRM делает в боевой Bitrix, и у неё есть последствия
// за пределами базы: на добавление сделки подписан бизнес-процесс 685 «Процесс для общей
// сделки» — стейт-машина из 14 состояний, которая раздаёт задания живым людям
// (`БИЗНЕС-ПРОЦЕССЫ_Bitrix.md` §1 и §6.4). Отменить это нельзя, удалить сделку из CRM
// тоже нельзя. Поэтому запись разбита на два раздельных шага, а не на один «Сохранить».

/**
 * Пробная отправка. Ядро проверяет ТО ЖЕ, что при настоящей записи — блок-лист полей
 * (`check_blocked`) и обязательное название, — но в Bitrix не пишет и возвращает поля,
 * которые записало бы (`dry` в `deal_write`, core_api.py).
 *
 * Зачем отдельный шаг, если форма и сама не даёт вводить закрытые поля. Список закрытых
 * полей у формы — из справочника `/fields`, а он кешируется на час и мог отстать от
 * блок-листа ядра. Проверка на сервере отвечает на «что будет записано» по состоянию
 * ядра СЕЙЧАС, и стоит она ноль последствий: ни одной записи в Bitrix.
 */
export const useDealDryRun = () =>
  useMutation({ mutationFn: (fields: Record<string, unknown>) => api.createDeal(fields, true) })

/** Сколько раз и с каким шагом спрашиваем ядро о новой сделке. */
const READBACK_TRIES = 4
const READBACK_DELAY_MS = 800

export interface DealCreated {
  /** id из Bitrix. Есть всегда, если запись прошла. */
  id: string
  /**
   * Сделка, перечитанная ИЗ ЯДРА (а не из ответа Bitrix). null — ядро её пока не отдаёт.
   * Экран обязан различать эти случаи: сделка создана в обоих, но во втором ядро о ней
   * ещё не знает, и вести человека в карточку бессмысленно.
   */
  deal: Deal | null
  /** Сколько попыток перечитки понадобилось — для честной подписи «ядро догнало не сразу». */
  tries: number
  /** Почему перечитка не удалась. Пусто, когда `deal` пришла. */
  readbackError: string
}

/**
 * Создание сделки. Возвращает управление ТОЛЬКО после того, как сделку удалось перечитать
 * из ядра (или после того, как перечитка окончательно не удалась).
 *
 * Почему перечитываем, хотя ядро и так возвращает сделку в ответе. В ответе лежит то, что
 * ядро только что получило от Bitrix напрямую (`bx_real('crm.deal.get')`), а запись этого
 * в Postgres в `deal_write` обёрнута в try и при ошибке только пишет в лог. То есть ответ
 * «ok» не доказывает, что сделка появилась В ЯДРЕ — а карточка читает именно ядро. Плюс
 * Bitrix успевает переписать название («#ID / …») и запустить БП, и первое чтение показывает
 * это состояние, а не то, что мы отправили.
 *
 * Ошибка перечитки НЕ превращается в ошибку мутации намеренно: сделка в этот момент уже
 * создана, и показать «не удалось» означало бы позвать человека создать её второй раз.
 */
export const useCreateDeal = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (fields: Record<string, unknown>): Promise<DealCreated> => {
      const r = await api.createDeal(fields)
      const id = String(r.id ?? '')
      if (!id || id === '0') throw new Error('ядро ответило «создано», но не вернуло id сделки')

      let last = ''
      for (let i = 1; i <= READBACK_TRIES; i += 1) {
        try {
          return { id, deal: await api.getDeal(id), tries: i, readbackError: '' }
        } catch (e) {
          // Сессия кончилась — это не «ядро не догнало», и повторы её не вернут.
          if (e instanceof Unauthorized) throw e
          last = (e as Error).message
          if (i < READBACK_TRIES) await new Promise((ok) => setTimeout(ok, READBACK_DELAY_MS))
        }
      }
      return { id, deal: null, tries: READBACK_TRIES, readbackError: last }
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['deals'] })
      // Карточка сделки откроется на уже прочитанных данных, без второго запроса.
      if (r.deal) qc.setQueryData(['deal', r.id], r.deal)
    },
  })
}

export const useLogin = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.login(username, password),
    onSuccess: (data) => {
      // /api/login отдаёт только пользователя, прав в его ответе нет. Кладём в кэш то, что
      // знаем, и сразу перечитываем /api/me: без этого первый экран после входа рисовался бы
      // без прав, то есть с меню всех разделов подряд.
      qc.setQueryData<Me>(['me'], { user: data.user, rights: null })
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export const useLogout = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      qc.setQueryData<Me>(['me'], { user: null, rights: null })
      qc.clear()   // не оставлять чужие данные в кэше после выхода
    },
  })
}
